/**
 * Progressive-web-app plumbing: the service worker, and whether there is signal.
 *
 * Kept out of the components that use it because none of it is React — it is browser APIs with
 * awkward lifecycles, and the hooks here are the whole of what the interface needs to know.
 *
 * There is deliberately no install prompt. `beforeinstallprompt` is left uncaptured so the browser
 * offers installation in its own way — the address-bar icon, the "Install app" menu item — for the
 * people who go looking for it. A card that appears over the cars asking to be added to a home
 * screen is an advertisement, and this site does not have those.
 */

import { useEffect, useState } from 'react';

/**
 * Registers the worker, and reports when a new one is waiting.
 *
 * Only in production. A service worker in front of the dev server caches the very assets Vite is
 * trying to hot-replace, and the result is an afternoon spent wondering why an edit did nothing.
 */
export function registerServiceWorker(onUpdateReady: () => void): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) {
    return;
  }

  // After load, not during it. Registration competes with the first render for the same connection,
  // and the visitor sees the page sooner if it waits its turn.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;

          if (!installing) {
            return;
          }

          installing.addEventListener('statechange', () => {
            // An installed worker with a controller already present means this is an update rather
            // than a first install — the difference between "there is a new version" and "the site
            // now works offline", which are not the same message.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              onUpdateReady();
            }
          });
        });
      })
      .catch(() => {
        /* A refused registration costs the offline shell and nothing else. */
      });
  });
}

/** Tells the waiting worker to take over, then reloads onto it. */
export function applyServiceWorkerUpdate(): void {
  navigator.serviceWorker?.getRegistration().then((registration) => {
    registration?.waiting?.postMessage('skip-waiting');

    // controllerchange fires once the new worker has taken over; reloading before that would just
    // reload onto the old one.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    });
  });
}

/**
 * Whether the browser thinks it is online.
 *
 * `navigator.onLine` is famously optimistic — it reports a connected network interface, not a
 * working internet — so this is used to explain a failure that has already happened rather than to
 * decide in advance whether to try.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
